import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { cleanLocation, mapsSearchUrl } from '@goaltree/backend/convex/lib/captureDates'
import { cn } from '@goaltree/ui/lib/utils'
import { useMutation } from 'convex/react'
import { ExternalLink, MapPin, Plus, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { DetailRow } from '@/components/detail-panel'
import { type PlaceCoords, PlaceInput } from '@/components/place-input'
import { patchNode } from '@/lib/nodes-optimistic'

export function LocationMeta({ location, className }: { location: string; className?: string }) {
  return (
    <span
      title={location}
      className={cn('flex min-w-0 max-w-[10rem] items-center gap-1 leading-none', className)}
    >
      <MapPin className="h-2.5 w-2.5 shrink-0" />
      <span className="truncate">{location}</span>
    </span>
  )
}

export function TaskLocationRow({
  taskId,
  location,
}: {
  taskId: Id<'nodes'>
  location?: string | null
}) {
  const { t } = useTranslation()
  const setLocation = useMutation(api.nodes.setTaskLocation).withOptimisticUpdate((localStore, a) =>
    patchNode(localStore, a.taskId, { location: cleanLocation(a.location) }),
  )
  const [editing, setEditing] = useState(false)

  const save = (next: string, coords?: PlaceCoords) => {
    setEditing(false)
    if (next.trim() === (location ?? '') && !coords) return
    setLocation({ taskId, location: next, ...(coords && { locationCoords: coords }) }).catch(
      () => {},
    )
  }

  return (
    <DetailRow label={t('inbox.rowLocation')}>
      {editing ? (
        <PlaceInput
          defaultValue={location ?? ''}
          onCommit={save}
          onCancel={() => setEditing(false)}
          ariaLabel={t('inbox.rowLocation')}
          placeholder={t('inbox.locationPlaceholder')}
          className="-mx-1 min-w-0 flex-1 rounded bg-muted/40 px-1 py-0.5 outline-none"
        />
      ) : location ? (
        <>
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="-mx-1 flex min-w-0 cursor-pointer items-center gap-1 rounded px-1 py-0.5 transition-colors hover:bg-muted"
          >
            <MapPin className="h-3 w-3 shrink-0" />
            <span className="truncate">{location}</span>
          </button>
          <a
            href={mapsSearchUrl(location)}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 text-primary-text hover:underline"
          >
            {t('inbox.openInMaps')}
            <ExternalLink className="h-3 w-3" />
          </a>
          <button
            type="button"
            aria-label={t('common.clear', { defaultValue: 'Clear' })}
            onClick={() => save('')}
            className="ml-auto flex cursor-pointer items-center rounded p-0.5 text-muted-foreground/60 transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="h-3 w-3" strokeWidth={2.5} />
          </button>
        </>
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="-mx-1 flex cursor-pointer items-center gap-1 rounded px-1 py-0.5 text-muted-foreground transition-colors hover:bg-muted"
        >
          {t('inbox.addLocation')}
          <Plus className="h-3 w-3" />
        </button>
      )}
    </DetailRow>
  )
}
